import "@infinite-table/infinite-react/index.css";
import {
	type InfiniteTablePropColumns,
	TreeDataSource,
	TreeGrid,
} from "@infinite-table/infinite-react";
import { PropertyTypes } from "epsm-api-types";
import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Spinner } from "@/components/Spinner";
import { INFINITY_TABLE } from "@/config";
import { getAlign } from "@/lib/utils";
import { settings } from "@/lib/utils/settings/settings";
import { DEFAULT_STATE_VALUES } from "@/shared/constants/common";
import { formatValue } from "../../helpers/FormattingHelpers";
import { UseData as UseCompanyData } from "../../Profile/services/UseData";
import { fiscalPeriodsGrouped } from "../config";
import { financialsTreeDataFinTemplate } from "../financialsTreeDataFinTemplate";
import { financialsTreeDataInduTemplate } from "../financialsTreeDataInduTemplate";
import { getSortedFinancialsData } from "../lib/helpers/getSortedFinancialsData";
import { UseData } from "../lib/services/UseData";
import type { Node, TableCell, TreeColumns } from "../types";
import {
	CompanyTemplate,
	type FinancialsState,
	type FinancialWithPeriod,
} from "../types";
import { BalanceSheetChart } from "./components/BalanceSheetChart";
import { FreeCashFlowChart } from "./components/FreeCashFlowChart";
import { FreeCashFlowFirmGrowthChart } from "./components/FreeCashFlowFirmGrowthChart";
import { IncomeStatementChart } from "./components/IncomeStatementChart";
import { PercentOfRevenuesChart } from "./components/PercentOfRevenuesChart";
import { ReturnOnEquityChart } from "./components/ReturnOnEquityChart";

export const Financials = ({ state }: { state: FinancialsState }) => {
	const { execute, isLoading, error } = UseData.create().useGetData();
	const {
		execute: executeCompanyData,
		isLoading: isLoadingCompanyData,
		data: companyData,
	} = UseCompanyData.create().useGetCompanyData();
	const ticker = state?.ticker ?? DEFAULT_STATE_VALUES.ticker;
	const fiscalPeriod = state.fiscalPeriod ?? DEFAULT_STATE_VALUES.fiscalPeriod;

	const [financialsData, setFinancialsData] = useState<FinancialWithPeriod[]>(
		[],
	);
	const [financialsTreeData, setFinancialsTreeData] = useState<Node[]>([]);
	const [columnOrder, setColumnOrder] = useState<string[]>([]);
	const [columns, setColumns] = useState<InfiniteTablePropColumns<TreeColumns>>(
		{},
	);

	useEffect(() => {
		if (!ticker) return;

		const getData = async () => {
			await executeCompanyData({
				params: {
					fields: ["ticker", "template"],
					filter: [{ name: "ticker", condition: "eq", value: ticker }],
				},
			});
		};

		getData();
	}, [ticker, executeCompanyData]);

	const currentTemplate = useMemo(
		() => companyData?.data?.[0]?.template as CompanyTemplate,
		[companyData],
	);

	useEffect(() => {
		if (!currentTemplate) return;

		setFinancialsTreeData(
			currentTemplate === CompanyTemplate.Fin
				? financialsTreeDataFinTemplate
				: financialsTreeDataInduTemplate,
		);
	}, [currentTemplate]);

	useEffect(() => {
		const getData = async () => {
			const fiscalPeriods = fiscalPeriodsGrouped[fiscalPeriod];

			const result = await execute({
				params: {
					filter: [
						{ name: "ticker", condition: "eq", value: ticker },
						{ name: "fiscal_period", condition: "in", value: fiscalPeriods },
					],
					sort: [{ name: "fiscal_year", order: 1 }],
				},
			});

			const sortedData = getSortedFinancialsData(
				result?.data || [],
				fiscalPeriod,
			);

			setFinancialsData(sortedData);
		};

		if (ticker) {
			getData();
		}
	}, [ticker, execute, fiscalPeriod]);

	const getColumns = useCallback(
		(
			setColumnOrder: Dispatch<SetStateAction<string[]>>,
		): InfiniteTablePropColumns<TreeColumns> => {
			if (!financialsData.length) return {};

			const columnConfig: InfiniteTablePropColumns<TreeColumns> = {};
			const columnOrder: string[] = [];

			columnOrder.push("fieldName");
			columnConfig.fieldName = {
				field: "fieldName",
				header: "",
				renderTreeIcon: true,
				renderValue: ({ rawValue }) => rawValue,
				defaultWidth: 200,
			};

			const dynamicColumnNames = Array.from(
				new Set(financialsData.map((record) => record.period)),
			);

			if (!dynamicColumnNames.length) return {};

			for (const dynamicColumnName of dynamicColumnNames) {
				columnOrder.push(dynamicColumnName);

				columnConfig[dynamicColumnName] = {
					field: dynamicColumnName,
					headerAlign: "start",
					align: getAlign("num"),
					header: dynamicColumnName,
					valueFormatter: ({ value }: { value: TableCell }) => {
						return formatValue(
							value?.value,
							PropertyTypes.NUMBER,
							value?.options,
							"-",
						);
					},
					className: ({ rawValue }: { rawValue: TableCell }) =>
						rawValue?.fncClassName?.(rawValue?.value) || "",
					defaultWidth: 70,
				};
			}

			setColumnOrder(columnOrder);
			return columnConfig;
		},
		[financialsData],
	);

	useEffect(() => {
		const columnsCall = () => {
			const result = getColumns(setColumnOrder);
			setColumns(result);
		};
		columnsCall();
	}, [getColumns]);

	const transformDataForTreeGrid = useCallback((): TreeColumns[] => {
		if (!financialsData.length || !financialsTreeData.length) {
			return [];
		}

		const extractNestedPropertyValue = (
			obj: Record<string, any>,
			path: string,
		): any => {
			return path.split(".").reduce((value, key) => {
				if (!value || typeof value !== "object") {
					return;
				}
				return value[key];
			}, obj);
		};

		const processNode = (node: Node): TreeColumns => {
			const result: TreeColumns = {
				fieldName: node.name,
			};

			if (financialsData) {
				for (const record of financialsData) {
					const fiscalKey = record.period;
					result[fiscalKey] = {
						value: node.field
							? extractNestedPropertyValue(record, node.field)
							: null,
						...(node.options && {
							options: node.options,
						}),
						...(node.fncClassName && {
							fncClassName: node.fncClassName,
						}),
					};
				}

				if (node.children && Array.isArray(node.children)) {
					result.children = node.children.map(processNode);
				}
			}

			return result;
		};

		const treeData = financialsTreeData.map(processNode);

		return treeData;
	}, [financialsData, financialsTreeData]);

	const treeData = transformDataForTreeGrid();
	const chartData = financialsData.toReversed().slice(-10);

	if (isLoading || isLoadingCompanyData) return <Spinner />;
	if (
		!(isLoading || isLoadingCompanyData) &&
		(!financialsData.length || error)
	) {
		return (
			<div className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
				{`Fundamental data not available for ${ticker}`}
			</div>
		);
	}

	return (
		<div
			className="h-full flex flex-col gap-5"
			data-testid="financials-component"
		>
			<div className="flex flex-wrap mt-3 gap-1">
				<IncomeStatementChart data={chartData} template={currentTemplate} />
				<PercentOfRevenuesChart data={chartData} />
				<BalanceSheetChart data={chartData} />
				<ReturnOnEquityChart data={chartData} />
				<FreeCashFlowChart data={chartData} />
				<FreeCashFlowFirmGrowthChart data={chartData} />
			</div>

			<div className="h-full min-h-[500px]">
				<TreeDataSource<TreeColumns>
					primaryKey="fieldName"
					data={treeData}
					nodesKey="children"
					loading={isLoading}
					defaultTreeExpandState={{
						defaultExpanded: false,
						expandedPaths: [
							["Financial Metrics"],
							["Financial Metrics", "Metrics"],
						],
					}}
				>
					<TreeGrid<TreeColumns>
						columns={columns}
						columnOrder={columnOrder}
						onColumnOrderChange={setColumnOrder}
						rowHeight={INFINITY_TABLE.ROW_HEIGHT}
						columnHeaderHeight={INFINITY_TABLE.COLUMN_HEADER_HEIGHT}
						licenseKey={settings.infiniteTableLicenseKey}
						columnDefaultFlex={1}
						headerOptions={{
							alwaysReserveSpaceForSortIcon: false,
						}}
						columnDefaultSortable={false}
						defaultColumnPinning={{ fieldName: "start" }}
						onCellClick={({ dataSourceApi, rowIndex }) => {
							const rowInfo = dataSourceApi!.getRowInfoByIndex(rowIndex);

							if (!rowInfo || !rowInfo.isTreeNode || !rowInfo.isParentNode) {
								return;
							}

							if (dataSourceApi!.treeApi.isNodeExpanded(rowInfo.nodePath)) {
								dataSourceApi!.treeApi.collapseNode(rowInfo.nodePath);
							} else {
								dataSourceApi!.treeApi.expandNode(rowInfo.nodePath);
							}
						}}
					/>
				</TreeDataSource>
			</div>
		</div>
	);
};
